import IconTextButton from '@/common/atoms/IconTextButton'
import Markdown from '@/common/atoms/Markdown'
import { EditorHandle } from '@/editor'
import CollabEditor from '@/editor/components/CollabEditor'
import {
  Box,
  Button,
  Collapse,
  Container,
  HStack,
  Heading,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useUpdateMeetingMutation } from '@gql'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  MagicIcon,
  MeetingSummaryShareIcon,
} from 'src/icons'
import settings from 'src/settings'
import { trpc } from 'src/trpc'
import { MeetingContext } from '../contexts/MeetingContext'
import useCanShareMeetingSummary from '../hooks/useCanShareMeetingSummary'
import useDownloadCircleSummaryPdf from '../hooks/useDownloadCircleSummaryPdf'
import MeetingSummaryShareModal from '../modals/MeetingSummaryShareModal'
import MeetingSummaryShares from './MeetingSummaryShares'

export default function MeetingPanelEnded() {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const toast = useToast()
  const showPanel = useDisclosure({ defaultIsOpen: true })
  const editorRef = useRef<EditorHandle>(null)

  const { meeting, canEdit, isEnded } = useContext(MeetingContext)!
  const { download: downloadPdf, downloading } = useDownloadCircleSummaryPdf()
  const shareModal = useDisclosure()
  const canShare = useCanShareMeetingSummary()

  const [updateMeeting] = useUpdateMeetingMutation()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generatedSummary, setGeneratedSummary] = useState<string | undefined>()

  // Saving an EMPTY summary is a valid outcome (Yohan, 11/08/2026): a meeting
  // may close without a word written, and nothing here should hold it hostage.
  // Before, the early return on an unchanged value made the button do strictly
  // nothing when the field was left empty — '' === '' — so the panel stayed in
  // write mode with no way out, and typing a single letter was the only exit.
  const handleSave = async () => {
    if (!editorRef.current || !meeting) return

    const value = editorRef.current.getValue()

    if (value !== meeting.summary) {
      setSaving(true)
      await updateMeeting({
        variables: {
          id: meeting.id,
          values: { summary: value },
        },
      })
    }

    // Leaving write mode is the button's job, not a side effect of the summary
    // having become non-empty.
    setSaving(false)
    setEditing(false)
  }

  const handleGenerate = () => {
    if (!meeting) return

    // Use generated summary we already have it
    if (generatedSummary) {
      editorRef.current?.setValue(generatedSummary)
      return
    }

    // Generate new summary
    setGenerating(true)
    trpc.ai.generateMeetingSummary
      .query({ meetingId: meeting.id, lang: language })
      .then((summary) => {
        editorRef.current?.setValue(summary)
        setGeneratedSummary(summary)
      })
      .catch((error) => {
        console.error(error)
        toast({
          title: t('common.error'),
          status: 'error',
          duration: 3000,
        })
      })
      .finally(() => setGenerating(false))
  }

  // Offer to write the summary ONCE, when the panel first sees the meeting.
  // Recomputing it on every meeting change reopened write mode behind the
  // user's back: any update to the meeting — and a subscription sends plenty —
  // put an empty-summary meeting straight back into editing, undoing the exit.
  const autoOpened = useRef(false)
  useEffect(() => {
    if (!meeting || autoOpened.current) return
    autoOpened.current = true
    setEditing(canEdit && !meeting.summary)
  }, [meeting, canEdit])

  if (!meeting || !isEnded || (!canEdit && !canShare && !meeting.summary)) {
    return null
  }

  return (
    <Container maxW="3xl" py={5}>
      <IconTextButton
        aria-label={
          showPanel.isOpen
            ? t('common.hide')
            : t('MeetingPanelEnded.headingRead')
        }
        icon={
          showPanel.isOpen ? (
            <ChevronDownIcon size="1em" />
          ) : (
            <ChevronUpIcon size="1em" />
          )
        }
        showText={!showPanel.isOpen}
        variant="ghost"
        position="absolute"
        top={0}
        right={0}
        onClick={showPanel.onToggle}
      />

      <Collapse in={showPanel.isOpen}>
        <VStack spacing={3} align="stretch">
          <HStack spacing={5}>
            <Heading as="h2" size="md">
              {editing
                ? t('MeetingPanelEnded.headingWrite')
                : t('MeetingPanelEnded.headingRead')}
            </Heading>
            {!editing && canEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
              >
                {t('common.edit')}
              </Button>
            )}
            {/* Also while writing: a meeting without a summary opens in this
                mode, and its compte rendu can be shared all the same */}
            {canShare && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<MeetingSummaryShareIcon size={16} />}
                onClick={shareModal.onOpen}
              >
                {t('MeetingPanelEnded.share')}
              </Button>
            )}
            {/* #131: the circle's compte rendu, whole, to reread or print */}
            <Button
              size="sm"
              variant="outline"
              leftIcon={<DownloadIcon size={16} />}
              isLoading={downloading === meeting.id}
              onClick={() => downloadPdf(meeting.id)}
            >
              {t('MeetingPanelEnded.downloadPdf')}
            </Button>
          </HStack>

          {editing ? (
            <>
              <Text color="gray.500" _dark={{ color: 'gray.300' }}>
                {t('MeetingPanelEnded.info')}
              </Text>

              <CollabEditor
                ref={editorRef}
                docId={`meeting-${meeting.id}-summary`}
                value={meeting.summary}
                autoFocus
                minH="6em"
              />

              <HStack justifyContent="end">
                {/* Shown unconditionally: it used to appear only when a summary
                    already existed, so the very first write had no way out at
                    all. Closing a meeting without writing anything is allowed. */}
                <Button
                  variant="ghost"
                  isDisabled={saving || generating}
                  onClick={() => setEditing(false)}
                >
                  {t('common.cancel')}
                </Button>
                {settings.aiEnabled && (
                  <Button
                    leftIcon={<MagicIcon />}
                    isDisabled={saving}
                    isLoading={generating}
                    onClick={handleGenerate}
                  >
                    {t('MeetingPanelEnded.generate')}
                  </Button>
                )}
                <Button
                  colorScheme="blue"
                  isLoading={saving}
                  isDisabled={generating}
                  onClick={handleSave}
                >
                  {t('common.save')}
                </Button>
              </HStack>
            </>
          ) : (
            <Box mb={3}>
              <Markdown>{meeting.summary}</Markdown>
            </Box>
          )}

          {canShare && <MeetingSummaryShares meetingId={meeting.id} />}
        </VStack>
      </Collapse>

      {shareModal.isOpen && (
        <MeetingSummaryShareModal
          isOpen
          meetingId={meeting.id}
          onClose={shareModal.onClose}
        />
      )}
    </Container>
  )
}
